import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Wrench, ShieldCheck, User, Calendar, Package } from 'lucide-react';
import { ApprovalBadge, typeLabel, USAGE_META } from './equipmentMeta';
import { PhotoStrip } from './PhotoStrip';

const Line = ({ icon: Icon, children }) => (
  <p className="text-[#A3A3A3] text-xs flex items-center gap-1.5"><Icon className="w-3 h-3 text-[#525252] shrink-0" />{children}</p>
);

const warrantyText = (a) => {
  const w = a.warranty || {};
  const parts = [];
  if (a.warranty_until) parts.push(`until ${String(a.warranty_until).slice(0, 10)}`);
  if (w.manufacturer_term) parts.push(`mfr ${w.manufacturer_term}`);
  if (w.manufacturer_through) parts.push(`mfr thru ${w.manufacturer_through}`);
  if (w.labor_term) parts.push(`labor ${w.labor_term}`);
  if (w.labor_through) parts.push(`labor thru ${w.labor_through}`);
  return parts.join(' · ');
};

// Read-only equipment card for the field Customer Profile.
export const EquipmentCard = ({ a, onResubmit }) => {
  const [open, setOpen] = useState(false);
  const w = warrantyText(a);
  return (
    <div data-testid={`field-asset-${a.id}`} className="bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2.5 space-y-1.5">
      <div className="flex items-start justify-between gap-2">
        <p className="text-white text-sm font-medium flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5 text-[#D4AF37]" />{typeLabel(a.type)}
          {(a.brand || a.model) && <span className="text-[#737373] text-xs font-normal">· {a.brand} {a.model}</span>}</p>
        <div className="flex items-center gap-1 shrink-0">
          {a.is_historical && <span className="text-[9px] uppercase tracking-wider text-[#737373] border border-[#262626] rounded px-1.5 py-0.5" data-testid={`field-asset-historical-${a.id}`}>On file</span>}
          <ApprovalBadge status={a.approval_status} testid={`field-asset-pending-${a.id}`} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-3 gap-y-0.5">
        {a.serial && <Line icon={Package}>S/N {a.serial}</Line>}
        {a.install_date && <Line icon={Calendar}>Installed {String(a.install_date).slice(0, 10)}</Line>}
        {a.installed_by && <Line icon={User}>By {a.installed_by}</Line>}
        {w && <Line icon={ShieldCheck}><span data-testid={`field-asset-warranty-${a.id}`}>Warranty {w}</span></Line>}
      </div>
      <p className="text-[11px] text-[#737373]" data-testid={`field-asset-source-${a.id}`}>
        Source: <span className="text-[#D4D4D4]">{a.equipment_source_label}</span>{a.supplier_name ? ` (${a.supplier_name})` : ''}
        {a.quantity > 1 ? ` · Qty ${a.quantity}` : ''}{a.location ? ` · ${a.location}` : ''}
      </p>
      {a.job && <p className="text-[11px] text-[#737373]" data-testid={`field-asset-job-${a.id}`}>Job: <span className="text-[#D4D4D4]">{a.job.service_type || 'Visit'}</span>{a.job.date ? ` · ${a.job.date}` : ''}{a.job.job_number ? ` · ${a.job.job_number}` : ''}</p>}
      {a.approval_status === 'sent_back' && (
        <div className="text-xs text-orange-300 bg-orange-500/10 border border-orange-500/40 rounded px-2 py-1.5 flex items-center justify-between gap-2">
          <span>Office: {a.review_note}</span>
          {a.can_resubmit && <button type="button" data-testid={`field-asset-resubmit-${a.id}`} onClick={() => onResubmit(a)} className="shrink-0 rounded border border-orange-400/60 px-2 py-0.5 text-orange-200 hover:bg-orange-500/20">Fix & Resubmit</button>}
        </div>
      )}
      <PhotoStrip photos={a.photos} testid={`field-asset-photos-${a.id}`} />
      {(a.service_history?.length > 0 || a.notes) && (
        <button type="button" data-testid={`field-asset-expand-${a.id}`} onClick={() => setOpen((o) => !o)} className="text-[11px] text-[#A3A3A3] hover:text-white flex items-center gap-1">
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} History & notes
        </button>
      )}
      {open && (
        <div className="border-t border-[#1f1f1f] pt-1.5 space-y-0.5" data-testid={`field-asset-history-${a.id}`}>
          {a.notes && <p className="text-xs text-[#A3A3A3] whitespace-pre-wrap">{a.notes}</p>}
          {(a.service_history || []).map((h, i) => (
            <p key={i} className="text-[11px] text-[#737373]">{h.date} — {typeLabel(h.event)}{h.technician ? ` by ${h.technician}` : ''}{h.notes ? ` · ${h.notes}` : ''}</p>
          ))}
          {a.consumption?.status && <p className="text-[10px] text-[#525252]">{USAGE_META[a.consumption.status] || a.consumption.status}</p>}
        </div>
      )}
    </div>
  );
};

export default EquipmentCard;
